// Tax types for the SaaS billing prototype.
// See plans/saas-billing/10-stripe-tax.md.
//
// Three display patterns:
//   - Pattern A (exclusive): subtotal + tax = total. US, GB.
//   - Pattern B (inclusive): total (incl. {rate}% VAT). NG, ZA, GH, KE.
//   - Pattern C (reverse_charge): subtotal + 0 tax = total (VAT reverse charge).
//
// `TaxPreviewInput.taxableAmountMinor` is ALREADY discount-adjusted.
// Callers subtract subscription.discountMinor before calling previewTax.

import { CountryCode, CurrencyCode } from './subscription.model';

export type TaxBasis = 'inclusive' | 'exclusive' | 'reverse_charge';
export type TaxCalculation = 'auto' | 'manual' | 'reverse_charge';

export interface TaxPreviewInput {
  countryCode: CountryCode;
  currency: CurrencyCode;
  // Already discount-adjusted by the caller. See doc decision log.
  taxableAmountMinor: number;
  customerTaxId?: TaxIdInput;
  // Optional caller hint; ignored by the mock.
  productCategory?: 'subscription' | 'top_up';
}

export interface TaxPreview {
  // The amount tax is computed against (subtotal post-discount).
  taxableAmountMinor: number;
  // The tax itself. 0 for reverse charge.
  taxAmountMinor: number;
  // E.g., 0.0825 for 8.25%. 0 for reverse charge.
  taxRate: number;
  // 'exclusive' = tax added on top; 'inclusive' = tax already inside total;
  // 'reverse_charge' = customer self-accounts (EU/GB B2B).
  taxBasis: TaxBasis;
  // Display label, e.g., "CA sales 8.25%", "NG VAT 7.5%".
  jurisdiction: string;
  // Short label, e.g., "VAT" or "Sales tax".
  label: string;
  calculation: TaxCalculation;
  // Populated when calculation === 'reverse_charge'.
  reverseChargeNote?: string;
}

export interface TaxIdInput {
  countryCode: CountryCode;
  // e.g., 'gb_vat', 'eu_vat', 'ng_tin', 'za_vat', 'gh_tin', 'ke_pin', 'us_ein'.
  type: string;
  // The number itself, normalized (uppercase, no spaces).
  value: string;
}

export type TaxIdError =
  | 'INVALID_FORMAT'
  | 'UNSUPPORTED_COUNTRY';

export interface TaxIdRecord {
  id: string;
  type: string;
  value: string;
  countryCode: CountryCode;
  valid: boolean;
  validationError?: string;
  createdAt: string;
}
